import Phaser from "phaser";
import { BASE_PROJECTILE_TINT, BOW_DURATION_MS, BOW_RANGE_PX, PROJECTILE_SPEED, PROJECTILE_TINT_BY_WEAPON, SPEAR_COOLDOWN_MS, SPEAR_RANGE_PX, TRIDENT_DURATION_MS, TRIDENT_RANGE_PX } from "../../game/constants";
import { WeaponType } from "../../data/enums";
import { FishBase } from "../fish/FishBase";

export class WeaponSystem {
    private readonly scene: Phaser.Scene;
    private readonly owner: Phaser.Physics.Arcade.Sprite;
    private readonly projectiles: Phaser.Physics.Arcade.Group;
    private lastThrowAt = 0;
    private activeWeapon = WeaponType.Spear;
    private weaponExpireAt = 0;

    constructor(scene: Phaser.Scene, owner: Phaser.Physics.Arcade.Sprite) {
        this.scene = scene;
        this.owner = owner;
        this.projectiles = scene.physics.add.group({
            classType: Phaser.Physics.Arcade.Image,
            maxSize: 40,
            runChildUpdate: false,
        });
    }

    getProjectileGroup(): Phaser.Physics.Arcade.Group {
        return this.projectiles;
    }

    update(): void {
        if (this.activeWeapon !== WeaponType.Spear && this.scene.time.now >= this.weaponExpireAt) {
            this.activeWeapon = WeaponType.Spear;
            this.weaponExpireAt = 0;
        }
    }

    applyWeaponPowerUp(weaponType: WeaponType.Trident | WeaponType.Bow): void {
        this.activeWeapon = weaponType;
        this.weaponExpireAt = this.scene.time.now + (weaponType === WeaponType.Trident ? TRIDENT_DURATION_MS : BOW_DURATION_MS);
    }

    getActiveWeapon(): WeaponType {
        return this.activeWeapon;
    }

    getRemainingWeaponMs(): number {
        return this.activeWeapon === WeaponType.Spear ? 0 : Math.max(0, this.weaponExpireAt - this.scene.time.now);
    }

    tryThrowAt(fish: FishBase): boolean {
        const now = this.scene.time.now;
        if (now - this.lastThrowAt < SPEAR_COOLDOWN_MS) {
            return false;
        }

        const distance = Phaser.Math.Distance.Between(this.owner.x, this.owner.y, fish.x, fish.y);
        this.lastThrowAt = now;

        if (distance > this.getCurrentRange()) {
            this.spawnProjectile(fish.x, fish.y, true);
            return false;
        }

        this.spawnProjectile(fish.x, fish.y, false);
        return true;
    }

    updateFishHoverFeedback(pointerWorldX: number, pointerWorldY: number, fishGroup: Phaser.GameObjects.Group): void {
        fishGroup.getChildren().forEach((child) => {
            const fish = child as FishBase;
            const isHover = fish.getBounds().contains(pointerWorldX, pointerWorldY);
            if (!isHover) {
                fish.clearTint();
                return;
            }

            const inRange = Phaser.Math.Distance.Between(this.owner.x, this.owner.y, fish.x, fish.y) <= this.getCurrentRange();
            if (inRange) {
                fish.setTint(0x9df6ff);
            } else {
                fish.setTint(0xff7777);
            }
        });
    }

    private spawnProjectile(targetX: number, targetY: number, miss: boolean): void {
        const projectile = this.projectiles.get(this.owner.x, this.owner.y, "projectile") as Phaser.Physics.Arcade.Image | null;
        if (!projectile) {
            return;
        }

        projectile.setActive(true);
        projectile.setVisible(true);
        projectile.setDepth(8);
        projectile.setCircle(projectile.width * 0.45);
        projectile.setTint(PROJECTILE_TINT_BY_WEAPON[this.activeWeapon] ?? BASE_PROJECTILE_TINT);

        const direction = new Phaser.Math.Vector2(targetX - this.owner.x, targetY - this.owner.y).normalize();
        projectile.setVelocity(direction.x * PROJECTILE_SPEED, direction.y * PROJECTILE_SPEED);

        if (miss) {
            this.scene.time.delayedCall(220, () => {
                projectile.setActive(false);
                projectile.setVisible(false);
                projectile.setVelocity(0, 0);
            });
        } else {
            this.scene.time.delayedCall(700, () => {
                projectile.setActive(false);
                projectile.setVisible(false);
                projectile.setVelocity(0, 0);
            });
        }
    }

    private getCurrentRange(): number {
        switch (this.activeWeapon) {
            case WeaponType.Trident:
                return TRIDENT_RANGE_PX;
            case WeaponType.Bow:
                return BOW_RANGE_PX;
            default:
                return SPEAR_RANGE_PX;
        }
    }
}
